import { useEffect, useState } from 'react';
import { useAuthContext } from '../../auth/AuthContext';
import { getTableauLicenseKeys, type TableauLicenseKey } from '../../service/form';
import { ApiError } from '../../utils/api-error';

const NO_AVAILABLE_KEYS_MESSAGE = 'No Tableau licence keys are available';

export default function TableauLicenseKeys({ formId }: { formId?: string }) {
  const { fetchJWT } = useAuthContext();
  const [licenses, setLicenses] = useState<TableauLicenseKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [keyOrdered, setKeyOrdered] = useState(false);

  useEffect(() => {
    let active = true;

    const loadLicenses = async () => {
      setLoading(true);
      setError(false);
      setKeyOrdered(false);
      try {
        const { idToken } = await fetchJWT();
        if (!idToken) throw new Error('Missing authentication token');
        const result = await getTableauLicenseKeys(idToken, formId);
        if (active) setLicenses(result.licenses);
      } catch (requestError) {
        if (active) {
          if (
            requestError instanceof ApiError &&
            requestError.detail === NO_AVAILABLE_KEYS_MESSAGE
          ) {
            setKeyOrdered(true);
          } else {
            setError(true);
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadLicenses();
    return () => {
      active = false;
    };
  }, [fetchJWT, formId]);

  if (loading) return <p className="govuk-body">Loading your Tableau licence key(s)...</p>;

  if (keyOrdered) return <p className="govuk-body">Key ordered - not yet available</p>;

  if (error) {
    return (
      <p className="govuk-error-message" role="alert">
        Unable to load your Tableau licence key(s). Try again later.
      </p>
    );
  }

  return (
    <dl className="govuk-summary-list">
      {licenses.map(({ email, key }) => (
        <div className="govuk-summary-list__row" key={key}>
          <dt className="govuk-summary-list__key">{email}</dt>
          <dd className="govuk-summary-list__value">{key}</dd>
        </div>
      ))}
    </dl>
  );
}
